import { useRouter } from 'next/router';
import { Heading, Stack } from '@global-ecom/nitro-uds/elements';

import { Styles } from 'ui/components/Styles';
import { usePdp } from 'hooks/usePdp';
import { useTranslate } from 'hooks/useTranslations';
import { Skeleton } from 'ui/components/Skeleton';
import { getSizesFromSizeGroups } from 'utils/products';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { event, AnalyticsEvents } from 'utils/analytics';
import { Variant } from '__generated__/graphql';
import {
  getGa4Item,
  getLastRelativeEntry,
  setGa4ItemInMap,
} from 'utils/ga4Items';

import { useFeature } from '../../../hooks/useFeature';

export const ProductColorPicker = () => {
  const router = useRouter();
  const translate = useTranslate();
  const { desiredSwatch, masterProduct, productIsOrderable, variation } =
    usePdp();

  const sizes = variation ? getSizesFromSizeGroups(variation.sizeGroups) : [];
  const variations = masterProduct?.variations || [];
  const { staticFeatures } = useSiteConfig();
  const { isPNARegion } = staticFeatures;
  const { pageviewEventHasFired } = usePageEventsContext();
  const isStyliticsEnabled = useFeature('OUTFITS_ENABLED');

  return (
    <>
      <Stack gap="xs">
        <Heading asChild size="sm" weight="bold">
          <h3>{translate('color')}</h3>
        </Heading>
        {variation?.colorName ? (
          <Heading asChild size="xxxs" weight="normal">
            <h4>
              {variation?.colorName}{' '}
              {!productIsOrderable && (
                <span className="sr-only">
                  :{' '}
                  {variation?.displayOutOfStock?.displayValue ||
                    translate('soldOut')}
                </span>
              )}
            </h4>
          </Heading>
        ) : (
          <Skeleton className="mt-2 w-24 h-4" />
        )}
      </Stack>
      <Styles
        desiredSwatch={desiredSwatch}
        variations={variations as Variant[]}
        onChange={swatch => {
          const attributeValue = variations?.find(
            variation => variation?.colorValue === swatch
          );
          if (pageviewEventHasFired) {
            event(AnalyticsEvents.GA4_CustomEvent, {
              event_name: AnalyticsEvents.CHANGE_COLOR,
              event_params: {
                item_id_ep: variation?.id,
                item_name_ep: variation?.name,
                color_code_ep: `a${attributeValue?.colorValue}`,
                style_number_ep: attributeValue?.masterId,
                is_gift: 'false',
              },
            });
            if (isPNARegion && isStyliticsEnabled) {
              event(AnalyticsEvents.GA4_CustomEvent, {
                event_name: AnalyticsEvents.GA4EC_Stylitics,
                event_params: {
                  user_action: AnalyticsEvents.CHANGE_COLOR,
                  item_id_ep: attributeValue?.id,
                  item_name_ep: attributeValue?.name,
                  stylitics_outfit_id: undefined,
                  stylitics_product_id: attributeValue?.id,
                },
              });
            }
          }

          const id = `${attributeValue?.masterId}_${attributeValue?.colorValue}`;
          //If already in the map, do nothing
          const currentGa4Item = getGa4Item(id);
          if (currentGa4Item) {
          } else {
            //In the Ga4ItemsMap I check if there is an relative entry (has the same masterProductId).
            //If that is the case, add an entry with the same value but different key.
            const lastRelativeEntry = getLastRelativeEntry(
              attributeValue?.masterId
            );
            if (lastRelativeEntry) {
              const [_, lastValue] = lastRelativeEntry;
              const newGa4Item = { ...lastValue };
              if (newGa4Item.cart) delete newGa4Item.cart;
              setGa4ItemInMap(id, newGa4Item, true);
            }
          }

          router.replace({ query: { ...router.query, swatch } }, '', {
            scroll: false,
          });
        }}
        isHydrating={sizes.length === 0}
        colors={masterProduct?.colors ?? undefined}
      />
    </>
  );
};
